import { Controller } from '@hotwired/stimulus'

export default class LoginEmailFirstController extends Controller<HTMLElement> {
  static targets = ['form', 'email', 'password', 'passwordStep', 'submitButton', 'submitLabel', 'spinner']
  static values = { checkEmailUrl: String, ssoUrl: String, nextLabel: String, loginLabel: String }

  declare readonly formTarget: HTMLFormElement
  declare readonly emailTarget: HTMLInputElement
  declare readonly passwordTarget: HTMLInputElement
  declare readonly passwordStepTargets: HTMLElement[]
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly submitLabelTarget: HTMLElement
  declare readonly spinnerTarget: HTMLElement
  declare readonly checkEmailUrlValue: string
  declare readonly ssoUrlValue: string
  declare readonly nextLabelValue: string
  declare readonly loginLabelValue: string

  private loginUrl = ''

  connect (): void {
    this.loginUrl = this.formTarget.getAttribute('action') ?? ''
  }

  async submit (event: SubmitEvent): Promise<void> {
    if (this.isPasswordStepShown()) {
      this.showWaiting()

      return
    }

    event.preventDefault()
    this.showWaiting()

    if (await this.isSsoEmail()) {
      this.formTarget.action = this.ssoUrlValue
      this.formTarget.submit()

      return
    }

    this.showPasswordStep()
  }

  changeEmail (): void {
    if (!this.isPasswordStepShown()) {
      return
    }

    this.passwordStepTargets.forEach(element => element.classList.add('d-none'))
    this.passwordTarget.value = ''
    this.submitLabelTarget.textContent = this.nextLabelValue
  }

  restoreAfterLeaving (event: PageTransitionEvent): void {
    if (!event.persisted) {
      return
    }

    this.showReady(this.isPasswordStepShown() ? this.loginLabelValue : this.nextLabelValue)
  }

  private async isSsoEmail (): Promise<boolean> {
    try {
      const response = await fetch(this.checkEmailUrlValue, {
        method: 'POST',
        body: new FormData(this.formTarget)
      })

      if (!response.ok) {
        return false
      }

      const { is_sso: isSso } = await response.json()

      return isSso === true
    } catch {
      return false
    }
  }

  private isPasswordStepShown (): boolean {
    return !this.passwordStepTargets[0].classList.contains('d-none')
  }

  private showPasswordStep (): void {
    this.formTarget.setAttribute('action', this.loginUrl)
    this.passwordStepTargets.forEach(element => element.classList.remove('d-none'))
    this.showReady(this.loginLabelValue)
    this.passwordTarget.focus()
  }

  private showWaiting (): void {
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.setAttribute('aria-busy', 'true')
    this.submitLabelTarget.classList.add('sr-only')
    this.spinnerTarget.classList.remove('d-none')
  }

  private showReady (label: string): void {
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.removeAttribute('aria-busy')
    this.submitLabelTarget.textContent = label
    this.submitLabelTarget.classList.remove('sr-only')
    this.spinnerTarget.classList.add('d-none')
  }
}
